import Loading from 'components/shared/Loading';
import moment from 'moment';
import * as React from 'react';
import Loadable from 'react-loadable';
import { FormGroup, Input, Label } from 'reactstrap';
import { localMoment } from 'util/Time';
import { getFriendlyDateRangePresetValueForDisplay, MultipleDateRangePresets } from '../providers/Constants';
import { MultipleRangedPresetValueFilterEditProps, RangedPresetValue } from '../providers/Types';
import FilterEditRequiredValidation from './FilterEditRequiredValidation';
import { FiltersListButtonGroup, FiltersListCloseButton, FiltersListAddButton, FiltersListValueButton } from './index';

const DateRangePicker = Loadable({
	loader: () =>
		import(/* webpackChunkName: "date-range-picker-component" */ 'react-dates/lib/components/DateRangePicker'),
	loading: Loading,
});

interface MultipleDateRangePresetFilterEditState {
	currentValue: string;
	customFromValue: moment.Moment | null;
	customToValue: moment.Moment | null;
	focusedInput: 'startDate' | 'endDate' | null;
	displayValidationError: boolean;
}

/**
 * Configures multiple date values within a filter
 */
class MultipleDateRangePresetFilterEdit extends React.Component<
	MultipleRangedPresetValueFilterEditProps,
	MultipleDateRangePresetFilterEditState
> {
	public static defaultProps = {
		value: [],
		enumValues: [],
	};

	constructor(props) {
		super(props);

		this.state = {
			currentValue: '',
			customFromValue: null,
			customToValue: null,
			focusedInput: null,
			displayValidationError: false,
		};
	}

	/**
	 * Update local state when the controlled text box is updated
	 *
	 * @private
	 * @memberof EnumDropDownFilterEdit
	 */
	private handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ currentValue: e.currentTarget.value });
	};

	/**
	 *
	 * @private
	 * @returns
	 * @memberof EnumDropDownFilterEdit
	 */
	private getValuesFromProps(): RangedPresetValue[] {
		if (!this.props.value) {
			return [];
		}

		return this.props.value.filter((value) => value.preset || value.fromValue || value.toValue);
	}

	/**
	 * Adds the value currently in state to the list of values selected.
	 *
	 * @private
	 * @memberof EnumDropDownFilterEdit
	 */
	private addCurrentValue = () => {
		const currentValue = this.state.currentValue;
		const values = this.getValuesFromProps();
		let currentValueDateRange: RangedPresetValue;

		if (currentValue === 'custom') {
			currentValueDateRange = {
				fromValue: this.state.customFromValue,
				toValue: this.state.customToValue,
			};
		} else {
			const presetFn = MultipleDateRangePresets[currentValue];
			if (!presetFn) {
				console.warn('Preset not found:', currentValue);
				return;
			}

			currentValueDateRange = presetFn(this.props.filter.options?.ignoreTimezone);
		}

		const isEmpty = currentValueDateRange.fromValue === null && currentValueDateRange.toValue === null;
		const validationError = !this.props.filter.nullable && isEmpty;

		this.setState({
			displayValidationError: validationError,
		});

		const isUnique =
			values.length === 0 ||
			values.findIndex(
				(value) =>
					value.preset === currentValueDateRange.preset ||
					(value.fromValue === currentValueDateRange.fromValue && value.toValue === currentValueDateRange),
			) === -1;

		this.setState({
			displayValidationError: validationError,
		});

		if (!validationError && isUnique) {
			this.props.onChange([...values, currentValueDateRange], false);
		}

		this.setState({
			currentValue: '',
			customToValue: null,
			customFromValue: null,
		});
	};

	/**
	 * Allow users to hit enter to add new values.
	 *
	 * @private
	 * @memberof EnumDropDownFilterEdit
	 */
	private handleOnKeyDown = (e: React.KeyboardEvent) => {
		if (e.which === 13) {
			e.preventDefault();
			this.addCurrentValue();
		}
	};

	/**
	 * Remove a specific value from the list
	 *
	 * @private
	 * @memberof EnumDropDownFilterEdit
	 */
	private removeValue = (fieldIndex: number) => {
		const values = this.getValuesFromProps();

		// let the parent component know what the new values are.
		this.props.onChange(
			values.filter((_, index) => index !== fieldIndex),
			false,
		);
	};

	public render() {
		const values = this.getValuesFromProps();
		const availableDateRanges = Object.keys(MultipleDateRangePresets).filter((key) => {
			return !values.some((value) => value.preset === key);
		});

		const filterActionClass =
			'filters-popover__actions ' + (values.length > 0 ? 'filters-popover__actions--divider' : '');

		return (
			<FormGroup>
				<Label for="date-select" className="visually-hidden">
					Select date
				</Label>
				<Input
					id="date-select"
					data-testid="data-select-multiple"
					type="select"
					autoFocus
					value={this.state.currentValue}
					onKeyDown={this.handleOnKeyDown}
					onChange={this.handleOnChange}
					className=""
					autoComplete="off"
				>
					<option value="" />
					{availableDateRanges.map((preset, index) => {
						const presetValue = MultipleDateRangePresets[preset]();
						return (
							<option key={index} value={preset}>
								{presetValue.displayName || presetValue.preset}
							</option>
						);
					})}
					<option value="custom">Custom Date Range...</option>
				</Input>
				{this.state.currentValue === 'custom' ? (
					<DateRangePicker
						showDefaultInputIcon
						showClearDates
						small
						startDateId={'fromDate'}
						startDate={this.state.customFromValue}
						endDateId={'toDate'}
						endDate={this.state.customToValue}
						focusedInput={this.state.focusedInput}
						isOutsideRange={() => false}
						minimumNights={0}
						displayFormat={() => 'M/D/YYYY'}
						onDatesChange={({
							startDate,
							endDate,
						}: {
							startDate: moment.Moment;
							endDate: moment.Moment;
						}) => {
							this.setState({
								customFromValue: startDate ? localMoment(startDate).startOf('day') : null,
								customToValue: endDate ? localMoment(endDate).endOf('day') : null,
							});
						}}
						onFocusChange={(focusedInput) => {
							this.setState({ focusedInput });
						}}
					/>
				) : null}

				<div className={filterActionClass}>
					<FiltersListAddButton onClick={this.addCurrentValue} />
				</div>

				{this.state.displayValidationError ? (
					<FilterEditRequiredValidation messageOverride="Field is required" />
				) : null}

				{values.map((value, index) => {
					const friendlyDateRangePresetValueForDisplay = getFriendlyDateRangePresetValueForDisplay(value);

					return (
						<React.Fragment key={index}>
							<FiltersListButtonGroup>
								<FiltersListValueButton>
									{friendlyDateRangePresetValueForDisplay}
								</FiltersListValueButton>
								<FiltersListCloseButton
									ariaLabel={`Remove ${friendlyDateRangePresetValueForDisplay} as a filter`}
									onClick={() => {
										this.removeValue(index);
									}}
								/>
							</FiltersListButtonGroup>{' '}
						</React.Fragment>
					);
				})}
				<p />
			</FormGroup>
		);
	}
}

export default MultipleDateRangePresetFilterEdit;
